import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEO } from "@/components/SEO";

const UserRegistrationPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEO 
        title="User Registration Policy"
        description="CSW User Registration Policy. Learn about account creation requirements and guidelines for joining our virtual study community."
        keywords="csw registration policy, account creation, user guidelines"
        canonicalUrl="/registration-policy/"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-secondary/5" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-display font-bold mb-6">
              <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
                User Registration Policy
              </span>
            </h1>
            <p className="text-lg text-muted-foreground">
              CSW – Chill Study Work App
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8 md:p-12">
            <div className="prose prose-lg dark:prose-invert max-w-none">
              
              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">1. Introduction</h2>
                <p className="text-muted-foreground leading-relaxed">
                  Welcome to CSW – Chill Study Work App ("CSW," "we," "our," or "us"). This User Registration Policy outlines the terms and conditions for registering and maintaining an account with our app. By registering an account, you agree to comply with this policy.
                </p>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">2. Eligibility</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Age Requirement:</strong> You must be at least 13 years old to register for an account with CSW. If you are under the age of 16, you must have the permission of a parent or legal guardian to use the app.</p>
                  <p><strong className="text-foreground">Compliance with Laws:</strong> You must comply with all applicable local, national, and international laws and regulations when registering and using our app.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">3. Account Registration</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Account Information:</strong> During the registration process, you will be required to provide accurate and complete information, including your name, email address, and any other necessary details. You are responsible for ensuring that this information remains accurate and up to date.</p>
                  <p><strong className="text-foreground">Email Verification:</strong> After registering, you will receive a verification email to confirm your email address. This step is mandatory to activate your account and prevent spam or bot registrations.</p>
                  <p><strong className="text-foreground">Username and Password:</strong> You must choose a unique username and password for your account. It is your responsibility to keep your login credentials confidential and secure.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">4. Account Use</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Personal Use:</strong> Your account is intended for your personal use only. You may not share your account with others or allow anyone else to access your account.</p>
                  <p><strong className="text-foreground">Prohibited Activities:</strong> You are not permitted to engage in any activity that violates our terms of service or any applicable laws, including but not limited to:</p>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li>Misrepresentation of your identity or providing false information.</li>
                    <li>Harassment, bullying, or threatening other users.</li>
                    <li>Posting or sharing inappropriate, offensive, or illegal content.</li>
                    <li>Engaging in any form of fraud or deceptive practices.</li>
                  </ul>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">5. Account Security</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Security Measures:</strong> We implement various security measures to protect your account. However, you are also responsible for safeguarding your login credentials and notifying us immediately if you suspect any unauthorized access to your account.</p>
                  <p><strong className="text-foreground">Account Suspension or Termination:</strong> We reserve the right to suspend or terminate your account at any time if we believe you have violated this policy, our terms of service, or engaged in any unauthorized or illegal activities.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">6. Privacy</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p><strong className="text-foreground">Data Collection:</strong> During registration, we collect personal information in accordance with our Privacy Policy. By registering, you consent to the collection, use, and processing of your data as described in the Privacy Policy.</p>
                  <p><strong className="text-foreground">Communication:</strong> We may send you emails related to your account, such as verification emails, updates, and notifications. You can manage your communication preferences through your account settings.</p>
                </div>
              </div>

              <div className="mb-12">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">7. Changes to This Policy</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update this User Registration Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. We encourage you to review this policy periodically.
                </p>
              </div>

              <div className="mb-0">
                <h2 className="text-2xl font-display font-bold text-foreground mb-4">8. Contact Information</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  If you have any questions or concerns about this User Registration Policy, please contact us at:
                </p>
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-6">
                  <p className="font-semibold text-foreground mb-2">CSW – Chill Study Work App</p>
                  <a href="mailto:info@csw.live" className="text-primary hover:text-primary/80 transition-colors">
                    info@csw.live
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default UserRegistrationPolicy;
